import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import Swal from "sweetalert2";
import { axiosConfig } from "../axiosAPI";
import PermissionGate from "../components/PermissionGate";
import { PERMISSIONS } from "../utils/permissionConstants";
import RideStatusBadge, { rideStatusVariant } from "../components/RideStatusBadge";
import RideDetailsMap from "./RideDetailsMap";

const RIDE_STATUS_TRANSITIONS = {
  Ended: ["Paid"],
  Pending: ["Cancelled"],
  Accepted: ["Cancelled"],
};

const CITY_RIDE_STATUS_TRANSITIONS = {
  Ended: ["Paid"],
  Pending: ["Cancelled"],
  Confirmed: ["Cancelled"],
};

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const trimValue = (value) => {
  const text = String(value ?? "").trim();
  return text || "—";
};

const formatFare = (value) =>
  `EGP ${Number(value ?? 0).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;

const dogNames = (item) =>
  [item?.dogName, item?.dog2Name, item?.dog3Name].filter(Boolean).join(", ") || "—";

function InfoField({ label, children }) {
  return (
    <div className="passenger-info-field">
      <span className="passenger-info-label">{label}</span>
      <span className="passenger-info-value">{children}</span>
    </div>
  );
}

function RideRequestDetailsContent({ item, type, detailsPath, listReturnTo }) {
  const navigate = useNavigate();
  const isCityRide = type === "city_ride";
  const [status, setStatus] = useState(item.status);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const allowedTransitions =
    (isCityRide ? CITY_RIDE_STATUS_TRANSITIONS : RIDE_STATUS_TRANSITIONS)[status] || [];

  const updateStatus = async (newStatus) => {
    const confirmation = await Swal.fire({
      icon: "warning",
      title: "Change ride status?",
      text: `This will change the ride status to "${newStatus}".`,
      showCancelButton: true,
      confirmButtonText: "Yes, change it",
    });
    if (!confirmation.isConfirmed) return;

    setUpdatingStatus(true);
    try {
      const base = isCityRide
        ? `${process.env.REACT_APP_BASE_URL}/dashboard/city-rides`
        : `${process.env.REACT_APP_BASE_URL}/dashboard/rides`;

      await axiosConfig.put(`${base}/${item.uuid}/status`, { status: newStatus });

      setStatus(newStatus);
      Swal.fire({
        icon: "success",
        title: "Success",
        text: "Ride status updated successfully",
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to update ride status",
      });
    } finally {
      setUpdatingStatus(false);
    }
  };

  const profileReturnState = {
    item,
    returnTo: listReturnTo,
  };

  const openPassenger = () => {
    if (!item?.passengerUuid) return;
    navigate(`/PassengerDetails/${item.passengerUuid}`, {
      state: {
        returnTo: detailsPath,
        returnState: profileReturnState,
      },
    });
  };

  const openDriver = () => {
    if (!item?.driverUuid) return;
    navigate(`/Details/${item.driverUuid}`, {
      state: {
        returnTo: detailsPath,
        returnState: profileReturnState,
      },
    });
  };

  const getGoogleMapsLink = (location) => {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location)}`;
  };

  const getGoogleMapsLinkFromCoords = (lat, lng) => {
    return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
  };

  const getLocationDisplay = (address, lat, lng) => {
    const trimmedAddress = String(address ?? "").trim();
    if (trimmedAddress) {
      return { text: trimmedAddress, link: getGoogleMapsLink(trimmedAddress) };
    }
    const latNum = Number(lat);
    const lngNum = Number(lng);
    if (Number.isFinite(latNum) && Number.isFinite(lngNum)) {
      return {
        text: `${latNum.toFixed(6)}, ${lngNum.toFixed(6)}`,
        link: getGoogleMapsLinkFromCoords(latNum, lngNum),
      };
    }
    return null;
  };

  return (
    <>
      <div className="passenger-profile-card">
        <div className="passenger-profile-card__header">
          <div className="passenger-profile-avatar">
            <i className="fa fa-road"></i>
          </div>
          <div>
            <h2 className="passenger-profile-name">Trip details</h2>
            <p className="passenger-profile-subtitle">{trimValue(item.type)}</p>
          </div>
          {allowedTransitions.length > 0 ? (
            <PermissionGate permission={PERMISSIONS.DASHBOARD.UPDATE_RIDE_STATUS}>
              <div className="ride-status-actions">
                {allowedTransitions.map((nextStatus) => (
                  <Button
                    key={nextStatus}
                    label={`Mark as ${nextStatus}`}
                    className={`small-button small-button--${rideStatusVariant(nextStatus)}`}
                    disabled={updatingStatus}
                    onClick={() => updateStatus(nextStatus)}
                  />
                ))}
              </div>
            </PermissionGate>
          ) : null}
        </div>

        <div className="passenger-info-grid">
          <InfoField label="Status">
            <RideStatusBadge status={status} />
          </InfoField>
          <InfoField label="Ride cost">{formatFare(item.fare)}</InfoField>
          <InfoField label="Date">{formatDateTime(item.date)}</InfoField>
          <InfoField label="Scheduled date">
            {item.dateScheduled ? formatDateTime(item.dateScheduled) : "Now"}
          </InfoField>
          <InfoField label="Passenger">
            {item.passengerUuid && item.passengerFullName ? (
              <a
                className="edit"
                href={`/PassengerDetails/${item.passengerUuid}`}
                onClick={(e) => {
                  e.preventDefault();
                  openPassenger();
                }}
              >
                {item.passengerFullName}
              </a>
            ) : (
              trimValue(item.passengerFullName)
            )}
          </InfoField>
          <InfoField label="Driver">
            {item.driverUuid && item.driverFullName ? (
              <a
                className="edit"
                href={`/Details/${item.driverUuid}`}
                onClick={(e) => {
                  e.preventDefault();
                  openDriver();
                }}
              >
                {item.driverFullName}
              </a>
            ) : (
              trimValue(item.driverFullName)
            )}
          </InfoField>
          <InfoField label="Pickup">
            {(() => {
              const pickup = getLocationDisplay(
                item.addressFrom,
                item.latitudeFrom,
                item.longitudeFrom
              );
              return pickup ? (
                <a className="edit" href={pickup.link} rel="noreferrer" target="_blank">
                  {pickup.text}
                </a>
              ) : (
                "—"
              );
            })()}
          </InfoField>
          <InfoField label="Drop-off">
            {(() => {
              const dropoff = getLocationDisplay(
                item.addressTo,
                item.latitudeTo,
                item.longitudeTo
              );
              return dropoff ? (
                <a className="edit" href={dropoff.link} rel="noreferrer" target="_blank">
                  {dropoff.text}
                </a>
              ) : (
                "—"
              );
            })()}
          </InfoField>
          <InfoField label="Dogs">{dogNames(item)}</InfoField>
          {isCityRide ? (
            <InfoField label="Passenger count">{trimValue(item.passengerCount)}</InfoField>
          ) : null}
          {isCityRide ? (
            <InfoField label="Comment">{trimValue(item.comment)}</InfoField>
          ) : null}
          <InfoField label="Rating">{item.rate != null ? item.rate : "—"}</InfoField>
          <InfoField label="Review">{trimValue(item.review)}</InfoField>
          <InfoField label="Ride ID">{trimValue(item.uuid)}</InfoField>
        </div>
      </div>

      <div className="mt-4">
        <RideDetailsMap item={item} type={type} />
      </div>
    </>
  );
}

export default RideRequestDetailsContent;
